import { useEffect, useMemo, useState } from 'react';
import { ParsedUrlQuery } from 'querystring';
import i18next from 'i18next';
import { useRouter } from 'next/router';
import { PresentVdpPageProps } from 'components/VdpPage';
import {
  fetchAdInfo,
  fetchVehicleWithExperience,
  FetchVehicleWithExperienceParams,
} from 'lib/AutolistAPI';
import { AdInfo } from 'lib/CarGurusAds';
import { VdpOrNotFoundProps } from 'pages/listings/[vin]';
import VehicleWithExperience from 'types/Apis/VehicleWithExperience';
import { isPaidParam } from 'lib/GoogleAnalytics';
import { DealerPage as DealerPageModel } from 'lib/models/DealerPage';
import useHashLocation from './useHashLocation';
import { useGeolocationData } from './useGeoLocation';

type VehicleApiParams = Omit<FetchVehicleWithExperienceParams, 'locale'>;

export function extractVehicleWithExperienceParamsFromQuery(
  query: ParsedUrlQuery
): VehicleApiParams {
  function q(paramName: string): string {
    return query[paramName] as string;
  }

  const apiParams: VehicleApiParams = {
    vin: q('vin'),
    radius: query.radius ? parseFloat(q('radius')) : undefined,
  };

  if (q('latitude')) apiParams.latitude = parseFloat(q('latitude'));
  if (q('longitude')) apiParams.longitude = parseFloat(q('longitude'));

  return apiParams;
}

export function buildVdpPageProps(
  query: ParsedUrlQuery,
  vehicleWithExp: VehicleWithExperience,
  adInfo: AdInfo | undefined
): PresentVdpPageProps | null {
  function q(paramName: string): string | null {
    return (query[paramName] as string) || null;
  }

  return {
    vehicle: vehicleWithExp.vehicle,
    experience: vehicleWithExp.experience || null,
    isPaid: isPaidParam(query?.p),
    firstVisitTracking: {
      campaign: q('firstVisitCampaign'),
      referrer: q('firstVisitReferrer'),
      page: q('firstVisitPage'),
    },
    utmTracking: {
      content: q('utm_content'),
      campaign: q('utm_campaign'),
      medium: q('utm_medium'),
      term: q('utm_term'),
      source: q('utm_source'),
    },
    adInfo: adInfo || null,
  };
}

export default function useBuildVdpPageProps(dealerPage?: DealerPageModel): VdpOrNotFoundProps {
  const { values } = useHashLocation();
  const [vehicleWithExp, setVehicleWithExp] = useState<VehicleWithExperience>();
  const [adInfo, setAdInfo] = useState<AdInfo>();
  const geolocation = useGeolocationData();
  const router = useRouter();

  const apiParams = useMemo<FetchVehicleWithExperienceParams>(() => {
    return {
      latitude: geolocation?.latitude,
      longitude: geolocation?.longitude,
      locale: i18next.language,
      ...extractVehicleWithExperienceParamsFromQuery(values),
    };
  }, [geolocation?.latitude, geolocation?.longitude, values]);

  useEffect(() => {
    async function fetchData(): Promise<void> {
      try {
        setVehicleWithExp(await fetchVehicleWithExperience(apiParams));
        setAdInfo(await fetchAdInfo(apiParams.vin));
      } catch (e) {
        if (e.message === 'Valid coordinates were not provided') {
          router.push(router.asPath.split('&vin')[0]);
        }
        console.error(e);
      }
    }

    if (!apiParams.vin) setVehicleWithExp(undefined);

    const hasRequiredFields =
      values.vin && geolocation?.latitude !== undefined && geolocation.longitude !== undefined;
    const shouldFetch = !vehicleWithExp || vehicleWithExp?.vehicle.vin !== values.vin;

    if (hasRequiredFields && shouldFetch) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.vin, geolocation, apiParams, vehicleWithExp]);

  if (vehicleWithExp?.vehicle && adInfo) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return { ...buildVdpPageProps(values, vehicleWithExp, adInfo), geolocation, dealerPage };
  }
  return { vehicle: null, dealerPage };
}
